import LedFeedback from '@components/common/Feedback/LedFeedback';
import { Box, Button, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import { AddHomeOutlined, HomeOutlined, Refresh } from '@mui/icons-material';
import React from 'react';
import styled from 'styled-components';
import ForsightAPI from '@API/index';
import PTZController from '@classes/PTZController';
import { v4 } from 'uuid';
import { useSelector } from 'react-redux';
import { IStoreState } from '@store/index';
import { KinesisVideo, KinesisVideoSignalingChannels } from 'aws-sdk';
import { first, last, sortBy } from 'lodash';
import { SignalingClient } from 'amazon-kinesis-video-streams-webrtc';
import API from '@API/index';
import Utils from '@utils/index';
import { useKeyPress } from '@hooks/KeyPress/KeyPressHooks';

export const Aim = styled.div`
  margin: 0 auto;
  width: 1.5rem;
  height: 1.5rem;
  position: relative; // Enables positioning of pseudo-elements

  &::before,
  &::after {
    content: '';
    position: absolute;
    background-color: white; // Set the color of the lines
  }

  &::before {
    width: 100%; // Horizontal line spans the full width
    height: 2px; // Thickness of the horizontal line
    top: 50%; // Center it vertically
    transform: translateY(-50%);
  }

  &::after {
    height: 100%; // Vertical line spans the full height
    width: 2px; // Thickness of the vertical line
    left: 50%; // Center it horizontally
    transform: translateX(-50%);
  }
`;


const DirectionContainer = styled.div`
  position: absolute;
  width: 7.5rem;
  height: 7.5rem;
  left: calc(50% - 3.75rem);
  bottom: calc(50% - 3.75rem);
  display: flex;
  flex-direction: column;

  background: rgba(50, 50, 50, 0.0);
  border-radius: 50%;
  border: 1px solid #FFFFFF;

  .MuiSvgIcon-root {
    visibility: hidden;
  }

  :hover {
    .MuiSvgIcon-root {
      visibility: visible;
    }

    background: rgba(50, 50, 50, 0.5);
  }

  svg {
    color: white;
  }

  -webkit-transition: background-color 200ms linear;
  -ms-transition: background-color 200ms linear;
  transition: background-color 200ms linear;

  z-index: 5;

  pointer-events: none;
`

const ArrowContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`

const ZoomContainer = styled.div`
  position: absolute;
  right: 2rem;
  bottom: 2rem;
  display: grid;
  grid-template-columns: min-content min-content min-content;
  grid-gap: 1rem;
  align-items: center;

  svg {
    color: black;
  }

  .MuiButton-contained {
    background-color: rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.8) !important;

    min-width: 0 !important;
  }

  z-index: 5;
`

const ConnectedUsersContainer = styled(Paper)`
  position: absolute;
  left: 1rem;
  top: 1rem;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  background-color: rgba(255, 255, 255, 0.5)

  svg {
    color: black;
  }

  .MuiButton-contained {
    background-color: rgba(255, 255, 255, 0.8) !important;
    background: rgba(255, 255, 255, 0.8) !important;

    min-width: 0 !important;
  }

  z-index: 5;
`

interface IPTZControlProps {
  videoStream: VideoStream;
  kvsName?: string;
  siteId?: string;
  onExitRequest?: () => void;
  wraperStyles?: React.CSSProperties;
  ptz?: { mjpegUrl?: string, onvifPassword?: string, onvifUser?: string };
  containerWidth: number;
  containerHeight: number;
  onTimeout?: () => void;
  disableWidthPadding?: boolean;
}

const RORTPTZControl: React.FC<IPTZControlProps> = (props) => {
  const {
    kvsName,
    siteId,
    ptz,
    videoStream,
    containerWidth,
    containerHeight,
    onTimeout,
    disableWidthPadding
  } = props;

  const isSuperAdministrator = useSelector((store: IStoreState) => store.session.isSuperAdministrator);
  const credentials = useSelector((store: IStoreState) => store.session.credentials);
  const userData = useSelector((store: IStoreState) => store.session.userData);

  const [error, setError] = React.useState<string | undefined>(undefined);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [loadingStep, setLoadingStep] = React.useState<{ progress: number, text: string }>({
    progress: 0,
    text: ""
  })
  const [commandFeedback, setCommandFeedback] = React.useState<boolean | undefined>(undefined);
  const [connectedUsers, setConnectedUsers] = React.useState<{ userId: string, name: string, timestamp: number }[]>([]);
  const [loadingTimer, setLoadingTimer] = React.useState<number | undefined>(undefined);

  const ptzRef = React.useRef(ptz);
  const siteIdRef = React.useRef(siteId);
  const kvsNameRef = React.useRef(kvsName);

  const intervalRef = React.useRef<any>(0);
  const pingRef = React.useRef<any>(0);
  const logicInterval = React.useRef<any>(0);
  const peerRef = React.useRef<RTCPeerConnection | null>(null);
  const signalRef = React.useRef<SignalingClient | null>(null);
  const subscriptionsRef = React.useRef<Map<string, { userId: string, name: string, timestamp: number }>>(new Map());
  const currentZoomRef = React.useRef<number>(0);
  const centerDivRef = React.useRef<HTMLDivElement | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const allowZoom = React.useRef<boolean>(true);
  const zoomActionPool = React.useRef<{ time: number, value: number }[]>([])
  const timerIntervalRef = React.useRef<any>(0);
  const stateRef = React.useRef<"error" | "loading" | "connected" | "idle">("idle");
  const onTimeoutRef = React.useRef(onTimeout);

  React.useEffect(() => { onTimeoutRef.current = onTimeout }, [onTimeout])

  useKeyPress('+', () => {
    ptzControllerRef.current.rzoom(100);
  }, [])

  useKeyPress('-', () => {
    ptzControllerRef.current.rzoom(-100);
  }, [])

  useKeyPress('ArrowUp', () => {
    ptzControllerRef.current.rtilt(1)
  }, [])

  useKeyPress('ArrowDown', () => {
    ptzControllerRef.current.rtilt(-1)

  }, [])

  useKeyPress('ArrowLeft', () => {
    ptzControllerRef.current.rpan(-1)
  }, [])

  useKeyPress('ArrowRight', () => {
    ptzControllerRef.current.rpan(1)
  }, [])

  const handleMessage = React.useCallback((message: { status?: number, response?: string }) => {
    if (message && message.status) {
      setCommandFeedback(true);
      setTimeout(() => {
        setCommandFeedback(false)
      }, 300)
    }
  }, [])

  const setCurrentPositionAsHome = React.useCallback(() => {
    ptzControllerRef.current.setHome();
  }, [])

  const goToHome = React.useCallback(() => {
    ptzControllerRef.current.goToHome();
  }, [])

  const handleClickEvent = React.useCallback((event: React.MouseEvent | undefined, zoomFactor?: number) => {
    if (centerDivRef && centerDivRef.current && event && videoRef && videoRef.current) {
      const imageElement = videoRef.current;
      const imageBounds = imageElement.getBoundingClientRect();

      // Get the actual intrinsic resolution of the video
      const videoWidth = imageElement.videoWidth;
      const videoHeight = imageElement.videoHeight;
      const videoAspectRatio = videoWidth / videoHeight;

      // Calculate the aspect ratio of the displayed video bounds
      const displayAspectRatio = imageBounds.width / imageBounds.height;

      // Adjust videoBounds to match the actual video content
      let adjustedVideoBounds: { x: number, y: number, width: number, height: number } = { ...imageBounds };

      if (displayAspectRatio > videoAspectRatio) {
        // Black bars on the left and right (letterboxing)
        const visibleWidth = imageBounds.height * videoAspectRatio;
        const offsetX = (imageBounds.width - visibleWidth) / 2;

        adjustedVideoBounds = {
          x: imageBounds.x + offsetX,
          y: imageBounds.y,
          width: visibleWidth,
          height: imageBounds.height,
        };

      } else if (displayAspectRatio < videoAspectRatio) {
        // Black bars on the top and bottom (pillarboxing)
        const visibleHeight = imageBounds.width / videoAspectRatio;
        const offsetY = (imageBounds.height - visibleHeight) / 2;

        adjustedVideoBounds = {
          x: imageBounds.x,
          y: imageBounds.y + offsetY,
          width: imageBounds.width,
          height: visibleHeight,
        };
      }

      const clickX = event.pageX - adjustedVideoBounds.x;
      const clickY = event.pageY - adjustedVideoBounds.y;

      const relativeX = clickX / adjustedVideoBounds.width;
      const relativeY = clickY / adjustedVideoBounds.height;

      const finalX = videoWidth * relativeX;
      const finalY = videoHeight * relativeY;

      if (zoomFactor) {
        ptzControllerRef.current.areazoom([Math.floor(finalX), Math.floor(finalY), Math.floor(zoomFactor)], videoWidth, videoHeight)
      } else {
        ptzControllerRef.current.center([Math.floor(finalX), Math.floor(finalY)], videoWidth, videoHeight)
      }
    }
  }, [centerDivRef, videoRef]);

  const handleWheelEvent = React.useCallback((event: React.WheelEvent | undefined) => {
    if (allowZoom.current === false) return;

    if (event && event.deltaY) {
      if (event.deltaY < 0) {
        zoomActionPool.current.push({ time: Date.now(), value: 50 })
      } else {
        zoomActionPool.current.push({ time: Date.now(), value: -50 })
      }

      setTimeout(() => {
        const l = last(zoomActionPool.current);
        if (l) {
          if (Date.now() - l.time > 300) {
            allowZoom.current = false;
            let sum = 0;
            zoomActionPool.current.forEach(e => sum += e.value);
            if (sum > 0) handleClickEvent(event, 100 + sum);
            else {
              ptzControllerRef.current.rzoom(sum);
            }
            zoomActionPool.current = [];
            currentZoomRef.current += sum;

            setTimeout(() => { allowZoom.current = true }, 500)
          }
        }
      }, 310)
    }
  }, [handleClickEvent])

  const ptzControllerRef = React.useRef(new PTZController(videoStream, ForsightAPI.PubSub, handleMessage))

  React.useEffect(() => { ptzControllerRef.current.processInitialRequest() }, [])

  const startLoadingTimer = React.useCallback(() => {
    setLoadingTimer(30);
    const interval = setInterval(() => {
      if (stateRef.current === "connected") {
        clearInterval(timerIntervalRef.current);
        return;
      }

      setLoadingTimer(timer => {
        if (timer === 0) {
          if (onTimeoutRef.current) {
            setError("WebRTC connection timeout... falling back to RPORT.")
            setLoading(false);
            clearInterval(timerIntervalRef.current);
            setTimeout(() => {
              if (onTimeoutRef.current) onTimeoutRef.current();
            }, 2000)
            return undefined;
          } else {
            setError("WebRTC connection timeout...")
            setLoading(false);
            clearInterval(timerIntervalRef.current);
            return undefined;
          }
        }

        if (timer) {
          if (timer === 0) return 0;
          return timer - 1;
        }

        return 0;
      })
    }, 1000)

    timerIntervalRef.current = interval;
  }, [])

  const perform = React.useCallback(async () => {
    if (!credentials) return;

    try {
      stateRef.current = "loading";
      startLoadingTimer();
      setError(undefined);
      setLoading(true);

      // <video> HTML elements to use to display the local webcam stream and remote stream from the master
      const localView = document.getElementById('webrtc-client') as HTMLVideoElement;

      setLoadingStep({
        progress: 0.0,
        text: "Opening tunnel..."
      })

      const wait = async function () {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve({})
          }, 2000)
        })
      }

      let edgeDevice: string = process.env.REACT_APP_STACK === 'prod' ? 'a8_a1_59_da_31_b1' : 'a8_a1_59_9e_d4_54';

      if (videoStream.iotThingName) {
        if (videoStream.iotThingName !== "DETACHED" && process.env.REACT_APP_STACK === 'prod') {
          edgeDevice = videoStream.iotThingName;
        }
      }

      API.PubSub.publishOnTopic(`edge/${edgeDevice}/http/requests/${v4()}`, {
        "device": {
          "manufacturer": "Axis",
          "type": "PTZ",
          "url": Utils.RegEx.findIPAddress(videoStream?.ptz?.mjpegUrl || "")
        },
        "action": "webrtc/init",
        "parameters": {
          "kvsName": kvsName
        }
      })

      await wait();

      const options = {
        credentials: credentials,
        region: process.env.REACT_APP_KINESIS_VIDEO_STREAMS_REGION || "",
        correctClockSkew: true
      }

      const kinesisVideoClient = new KinesisVideo(options);

      setLoadingStep({
        progress: 0.2,
        text: "Getting channel information..."
      })

      const describeSignalingChannelResponse = await kinesisVideoClient
        .describeSignalingChannel({
          ChannelName: kvsName,
        })
        .promise();

      const channelARN = describeSignalingChannelResponse?.ChannelInfo?.ChannelARN || "";

      setLoadingStep({
        progress: 0.4,
        text: "Getting endpoint..."
      })

      const getSignalingChannelEndpointResponse = await kinesisVideoClient
        .getSignalingChannelEndpoint({
          ChannelARN: channelARN,
          SingleMasterChannelEndpointConfiguration: {
            Protocols: ['HTTPS', 'WSS'],
            Role: "VIEWER",
          },
        })
        .promise();
      const endpoint = first(getSignalingChannelEndpointResponse.ResourceEndpointList)?.ResourceEndpoint || "";

      const kinesisVideoSignalingChannelsClient = new KinesisVideoSignalingChannels({
        ...options,
        endpoint: endpoint,
      });

      setLoadingStep({
        progress: 0.6,
        text: "Getting ice configuration..."
      })

      const getIceServerConfigResponse = await kinesisVideoSignalingChannelsClient
        .getIceServerConfig({
          ChannelARN: channelARN,
        })
        .promise();
      const iceServers = [
        { urls: `stun:stun.kinesisvideo.${process.env.REACT_APP_KINESIS_VIDEO_STREAMS_REGION}.amazonaws.com:443` }
      ];
      getIceServerConfigResponse?.IceServerList?.forEach(iceServer =>
        iceServers.push({
          //@ts-ignore
          urls: iceServer.Uris,
          username: iceServer.Username,
          credential: iceServer.Password,
        }),
      );

      setLoadingStep({
        progress: 0.7,
        text: "Opening WebRTC connection..."
      })

      const peerConnection = new RTCPeerConnection({ iceServers });
      peerRef.current = peerConnection;

      const signalingClient = new SignalingClient({
        channelARN,
        channelEndpoint: last(getSignalingChannelEndpointResponse.ResourceEndpointList)?.ResourceEndpoint || "",
        role: "VIEWER" as any,
        ...options,
        clientId: v4(),
        systemClockOffset: kinesisVideoClient.config.systemClockOffset,
      });

      signalRef.current = signalingClient;

      signalingClient.on('open', async () => {
        const offer = await peerConnection.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
        });
        await peerConnection.setLocalDescription(offer);
        if (peerConnection.localDescription)
          signalingClient.sendSdpOffer(peerConnection.localDescription);

        setLoadingStep({
          progress: 0.8,
          text: "Waiting for remote tracks..."
        })
      });

      signalingClient.on('sdpAnswer', async answer => {
        await peerConnection.setRemoteDescription(answer);
      });

      signalingClient.on('iceCandidate', candidate => {
        peerConnection.addIceCandidate(candidate);
      });

      signalingClient.on('close', () => {
        // Handle client closures
      });

      signalingClient.on('error', error => {
        // Handle client errors
        setError(Utils.Error.getErrorMessage(error))
      });

      peerConnection.addEventListener('icecandidate', ({ candidate }) => {
        if (candidate) {
          signalingClient.sendIceCandidate(candidate);
        } else {
          // No more ICE candidates will be generated
        }
      });

      peerConnection.addEventListener('track', event => {
        setLoadingStep({
          progress: 0.9,
          text: "Adding tracks..."
        })
        localView.srcObject = event.streams[0];
      });

      intervalRef.current = setInterval(() => {
        API.PubSub.publishOnTopic(`edge/${edgeDevice}/http/requests/${v4()}`, {
          "device": {
            "manufacturer": "Axis",
            "type": "PTZ",
            "url": Utils.RegEx.findIPAddress(videoStream?.ptz?.mjpegUrl || "")
          },
          "action": "webrtc/heartbeat"
        })
      }, 60000)

      pingRef.current = setInterval(() => {
        const topicName: string = `${process.env.REACT_APP_STACK}/dashboard/updates/ptz/${kvsName}`;
        API.PubSub.publishOnTopic(topicName, { userId: userData?.userId, name: userData?.familyName ? `${userData.givenName} ${userData.familyName}` : `${userData?.givenName}`, isSuperAdministrator: isSuperAdministrator })
      }, 5000)

      signalingClient.open();

    } catch (error: any) {
      setError(Utils.Error.getErrorMessage(error));
      setLoading(false);
      console.log(error);
    }
  }, [credentials, startLoadingTimer, videoStream, kvsName, userData, isSuperAdministrator])

  React.useEffect(() => {
    perform();
    const topicName: string = `${process.env.REACT_APP_STACK}/dashboard/updates/ptz/${kvsName}`;
    const sub = API.PubSub.subscribeToTopic(topicName, (message: any) => {
      if (message.isSuperAdministrator === false) subscriptionsRef.current.set(message.userId, { userId: message.userId, name: message.name, timestamp: Date.now() })
    })

    logicInterval.current = setInterval(() => {
      const users: any[] = [];
      subscriptionsRef.current.forEach((value, key) => {
        if ((Date.now() - value.timestamp) < 10000) {
          users.push(value);
        }
      })
      setConnectedUsers(users);
    }, 1000)

    ptzControllerRef.current.connect();

    return () => {
      if (ptzControllerRef.current) {
        sub.unsubscribe();
        clearInterval(intervalRef.current)
        clearInterval(pingRef.current);
        clearInterval(logicInterval.current);
        clearInterval(timerIntervalRef.current)
        if (peerRef.current) {
          peerRef.current.close()
        }

        if (signalRef.current) {
          signalRef.current.close();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        ptzControllerRef.current.disconnect();
      }
    }
  }, [kvsName, perform])

  React.useEffect(() => { siteIdRef.current = siteId }, [siteId]);
  React.useEffect(() => { kvsNameRef.current = kvsName }, [kvsName])
  React.useEffect(() => { ptzRef.current = ptz }, [ptz])

  return (
    <Box
      position="absolute"
      zIndex={1000}
      width={disableWidthPadding === true ? `${containerWidth}px` : `calc(${containerWidth}px - 2rem)`}
      height={`calc(${containerHeight}px - 0.1rem)`}
      bgcolor="black"
    >
      <Box
        width="100%"
        height="100%"
        position="relative"
        display="flex"
        justifyContent="center"
        alignItems="center"
        zIndex={1000}
      >
        <Box
          bgcolor="transparent"
          height="100%"
          width="100%"
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          position="relative"
        >
          {loading === false && error === undefined &&
            <>
              <DirectionContainer id="ptz-control-description">
                <ArrowContainer>
                  <Box
                    width="50%"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                    display="flex"
                  >
                    <Box height="100%" display="flex" flexDirection="row" alignItems="center" justifyContent="center" borderRadius="50%" padding="1rem" position="absolute" >
                      {commandFeedback ?
                        <LedFeedback
                          active={commandFeedback}
                        />
                        : (
                          <Aim />
                        )}
                    </Box>
                  </Box>
                </ArrowContainer>
              </DirectionContainer>
              <ZoomContainer id="zoom-control-description">
                <Tooltip arrow placement="top" title="Go to home position">
                  <Button
                    onClick={() => { goToHome() }}
                    variant="contained"
                  >
                    <HomeOutlined />
                  </Button>
                </Tooltip>
                <Tooltip placement="top" arrow title="Set current position as home">
                  <Button disabled={isSuperAdministrator === false} onClick={() => { setCurrentPositionAsHome() }} variant="contained" data-cy="timeline-ptz-set-home-button" >
                    <AddHomeOutlined />
                  </Button>
                </Tooltip>
              </ZoomContainer>
              <ConnectedUsersContainer>
                <Box width="100%" display="flex" alignItems="center">
                  <Typography variant="caption">Connected users</Typography>
                </Box>
                {sortBy(connectedUsers, 'name').map((e) => (
                  <Box display="grid" gridTemplateColumns="max-content max-content" gap="0.5rem" alignItems="center">
                    <Box
                      width="0.5rem"
                      height="0.5rem"
                      borderRadius="50%"
                      bgcolor="limegreen"
                    />
                    <Typography variant="caption">{e.userId === userData?.userId ? 'You' : e.name}</Typography>
                  </Box>
                ))}
              </ConnectedUsersContainer>
            </>
          }
          <video
            ref={videoRef}
            onClick={handleClickEvent}
            onWheel={(e) => handleWheelEvent(e)}
            autoPlay
            onLoadedData={() => {
              setLoading(false);
              stateRef.current = "connected";
            }}
            id="webrtc-client"
            style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'absolute' }}
          />
          <div ref={centerDivRef} style={{ position: 'absolute' }} />
          {loading === true && error === undefined &&
            <Box>
              <Box width="15rem" display="flex" flexDirection="row" alignItems="center" justifyContent="center">
                <Typography color="white" >{`${loadingStep.text}`}</Typography>
              </Box>
              <LinearProgress color="info" variant="determinate" value={loadingStep.progress * 100} style={{ width: '15rem' }} />
              <Box width="15rem" display="flex" flexDirection="row" alignItems="center" justifyContent="center">
                <Typography color="white" >{`${loadingTimer}`}</Typography>
              </Box>
            </Box>
          }
          {error !== undefined && loading === false &&
            <Box display="flex" flexDirection="column">
              <Box>
                <Typography variant="body2" style={{ color: 'white' }} >{error}</Typography>
              </Box>
              {!error.includes('RPORT') &&
                <Box display="flex" width="100%" justifyContent="center" alignItems="center" flexDirection="row">
                  <Button onClick={perform} style={{ color: 'white' }} variant="text">
                    <Typography>Retry</Typography>
                    <Refresh />
                  </Button>
                </Box>
              }
            </Box>
          }
        </Box>
      </Box >
    </Box>
  )
}

export default RORTPTZControl;
